import ApiService from "@/services/api.service";
import { StorageService } from "@/services/storage.service";

class AuthenticationError extends Error {
  constructor(errorCode, message) {
    super(message);
    this.name = this.constructor.name;
    this.message = message;
    this.errorCode = errorCode;
  }
}

class TooManyRequestsError extends Error {
  constructor(errorCode, message) {
    super(message);
    this.name = this.constructor.name;
    this.message = message;
    this.errorCode = errorCode;
  }
}

class RecruiterNotFoundError extends Error {
  constructor(errorCode, message) {
    super(message);
    this.name = this.constructor.name;
    this.message = message;
    this.errorCode = errorCode;
  }
}

class ValidationError extends Error {
  constructor(errorCode, fields) {
    super(fields);
    this.name = this.constructor.name;
    this.message = fields;
    this.errorCode = errorCode;
    this.fields = fields;
  }
}

class InvitationNotExisting extends Error {
  constructor(errorCode, message) {
    super(message);
    this.name = this.constructor.name;
    this.message = message;
    this.errorCode = errorCode;
  }
}

class InvitationAlreadyUsed extends Error {
  constructor(errorCode, message) {
    super(message);
    this.name = this.constructor.name;
    this.message = message;
    this.errorCode = errorCode;
  }
}

const UserService = {
  /**
   * Login the user and store the access token to TokenService
   * @param username
   * @param password
   * @returns {
   *     expires,
   *     token
   * }
   * @throws AuthenticationError
   */
  login: async function(username, password) {
    try {
      const response = await ApiService.post(
        "/auth/login",
        {
          username: username,
          password: password
        },
        {
          auth: {
            username: username,
            password: password
          }
        }
      );
      StorageService.saveToken(response.data.token);
      ApiService.setHeader();
      return response.data;
    } catch (error) {
      if (error.response.status === 429) {
        throw new TooManyRequestsError(
          error.response.status,
          error.response.data
        );
      } else if (error.response.status === 401) {
        throw new AuthenticationError(
          error.response.status,
          error.response.data
        );
      }
    }
  },

  resetPassword: async function(newPassword, newPasswordConfirm) {
    try {
      await ApiService.post("/auth/reset-password", {
        new_password: newPassword,
        new_password_confirm: newPasswordConfirm
      });
    } catch (error) {
      if (error.response.status === 422) {
        throw new ValidationError(error.response.status, error.response.data);
      } else if (error.response.status === 401) {
        throw new AuthenticationError(
          error.response.status,
          error.response.data
        );
      }
    }
  },

  /**
   * Getting Recruiter Contact Information by invitation uid
   * @param uid
   * @returns {
   *      "name",
   *      "phone"
   * }
   * @throws RecruiterNotFoundError
   */
  recruiterForInvitation: async function(uid) {
    try {
      const response = await ApiService.get(`/recruiter-for-invitation/${uid}`);
      return response.data;
    } catch (error) {
      if (error.response.status === 404) {
        throw new RecruiterNotFoundError(
          error.response.status,
          error.response.data
        );
      } else if (error.response.status === 400) {
        throw new Error("malformed request syntax");
      } else if (error.response.status === 401) {
        throw new AuthenticationError(
          error.response.status,
          error.response.data
        );
      }
    }
  },

  /**
   * Getting state about showing upadate availability popup
   * @param inv_uid
   */
  checkUpdateAvailabilityNeed: async function(inv_uid) {
    try {
      if (inv_uid !== "undefined") {
        const response = await ApiService.get(
          `/check-update-availability-need?inv_uid=${inv_uid}`
        ).catch(() => {});
        return response.data;
      }
    } catch (error) {
      if (error.response.status === 404) {
        throw new RecruiterNotFoundError(
          error.response.status,
          error.response.data
        );
      } else if (error.response.status === 400) {
        throw new Error("malformed request syntax");
      } else if (error.response.status === 401) {
        throw new AuthenticationError(
          error.response.status,
          error.response.data
        );
      }
    }
  },

  updateAvailability: async function(uid, date) {
    try {
      const response = await ApiService.post("/update-availability", {
        inv_uid: uid,
        availability_date: date
      });
      return response.data;
    } catch (error) {
      if (error.response.status === 422) {
        throw new ValidationError(error.response.status, error.response.data);
      } else if (error.response.status === 401) {
        throw new AuthenticationError(
          error.response.status,
          error.response.data
        );
      }
    }
  },

  validateInvitation: async function(uid) {
    try {
      const response = await ApiService.get(`/validate-invitation/${uid}`);
      return response.data;
    } catch (error) {
      if (error.response.status === 404) {
        throw new InvitationNotExisting(
          error.response.status,
          error.response.data
        );
      } else if (error.response.status === 422) {
        throw new InvitationAlreadyUsed(
          error.response.status,
          error.response.data
        );
      } else if (error.response.status === 401) {
        throw new AuthenticationError(
          error.response.status,
          error.response.data
        );
      }
    }
  },

  getFilter: async function() {
    try {
      const response = await ApiService.get("user/filter");
      return response.data;
    } catch (error) {
      if (error.response.status === 401) {
        throw new AuthenticationError(
          error.response.status,
          error.response.data
        );
      }
    }
  },

  getAnonymisedFilter: async function(language) {
    try {
      let response;
      const params = new URLSearchParams();
      params.append("set_request_language", language);
      response = await ApiService.get(
        "user/filter-anonymised?" + params.toString()
      );
      return response.data;
    } catch (error) {
      if (error.response.status === 401) {
        throw new AuthenticationError(
          error.response.status,
          error.response.data
        );
      }
    }
  },

  setFilter: async function(
    availability_date,
    one_patient,
    two_patients,
    further_persons_household,
    patients_fully_mobile,
    patients_wheelchair,
    patients_bedridden,
    night_commitment_none,
    night_commitment_once,
    night_commitment_multiple,
    dementia_no,
    dementia_yes,
    environment_urban,
    environment_rural,
    car_required_no,
    car_required_yes,
    building_type_apartment,
    building_type_house,
    own_room,
    own_toilet,
    compensation_option,
    gender_option,
    location_option
  ) {
    try {
      const response = await ApiService.post("user/filter", {
        availability_date,
        one_patient,
        two_patients,
        further_persons_household,
        patients_fully_mobile,
        patients_wheelchair,
        patients_bedridden,
        night_commitment_none,
        night_commitment_once,
        night_commitment_multiple,
        dementia_no,
        dementia_yes,
        environment_urban,
        environment_rural,
        car_required_no,
        car_required_yes,
        building_type_apartment,
        building_type_house,
        own_room,
        own_toilet,
        compensation_option,
        gender_option,
        location_option
      });
      return response.data;
    } catch (error) {
      if (error.response.status === 401) {
        throw new AuthenticationError(
          error.response.status,
          error.response.data
        );
      } else if (error.response.status === 422) {
        throw new ValidationError(error.response.status, error.response.data);
      }
    }
  },

  verifyInvitationName: async function(uid, lastName) {
    try {
      const response = await ApiService.post("/verify-invitation-name", {
        invitation_uid: uid,
        last_name: lastName
      });
      return response.data;
    } catch (error) {
      if (error.response.status === 422) {
        throw new ValidationError(error.response.status, error.response.data);
      } else if (error.response.status === 401) {
        throw new AuthenticationError(
          error.response.status,
          error.response.data
        );
      }
    }
  },

  setLanguage: async function(language) {
    try {
      const response = await ApiService.post("/user/set-language", {
        language: language
      });
      return response.data;
    } catch (error) {
      if (error.response.status === 401) {
        throw new AuthenticationError(
          error.response.status,
          error.response.data
        );
      }
    }
  },

  /**
   * Register user
   * @param invitation_uid
   * @param last_name
   * @param password
   * @param username
   * @returns {Promise<T>}
   * @throws AuthenticationError
   */
  register: async function(invitation_uid, last_name, password, username) {
    try {
      await ApiService.post(
        "/register",
        {
          invitation_uid: invitation_uid,
          last_name: last_name,
          password: password,
          username: username
        },
        {
          auth: {
            username: username,
            password: password
          }
        }
      );
    } catch (error) {
      if (error.response.status === 422) {
        throw new ValidationError(error.response.status, error.response.data);
      } else if (error.response.status === 401) {
        throw new AuthenticationError(
          error.response.status,
          error.response.data
        );
      } else if (error.response.status === 404) {
        throw new InvitationNotExisting(
          error.response.status,
          error.response.data
        );
      }
    }
  },

  /**
   */
  applications: async function() {
    try {
      const response = await ApiService.get("/applications");
      return response.data;
    } catch (error) {
      throw new AuthenticationError(error.response.status, error.response.data);
    }
  },

  /**
   */
  favorites: async function() {
    try {
      const response = await ApiService.get("/missions/favorites");
      return response.data;
    } catch (error) {
      throw new AuthenticationError(error.response.status, error.response.data);
    }
  },

  /**
   */
  acceptEmploymentAgreement: async function() {
    try {
      const response = await ApiService.post("/user/employment-agreement", {});
      return response.data;
    } catch (error) {
      throw new AuthenticationError(error.response.status, error.response.data);
    }
  },

  /**
   * Logout the current by removing the token from storage
   */
  logout: async function() {
    try {
      StorageService.removeToken();
      ApiService.removeHeader();
    } catch (error) {
      throw new AuthenticationError(error.response.status, error.response.data);
    }
  },

  /**
   * Returns the current user object
   */
  user: async function() {
    try {
      let response = await ApiService.get("/user");
      return response.data;
    } catch (error) {
      throw new AuthenticationError(error.response.status, error.response.data);
    }
  }
};

export default UserService;

export {
  UserService,
  AuthenticationError,
  TooManyRequestsError,
  RecruiterNotFoundError,
  ValidationError,
  InvitationNotExisting,
  InvitationAlreadyUsed
};
